import { useState, useEffect } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ButtonOne from '../../common/normal-button';
import { ColorsConstants } from '../../cssUtils/constants';
import CampaignCreate from './create/CampaignCreate';
import CTWAManage from './CTWAManage';
import CTWACampaignList from './CTWACampaignList';
import CTWAAuditLog from './CTWAAuditLog';
import {
  ArrowForward,
  Facebook,
  Instagram,
  ManageAccounts,
  Campaign,
  PhoneAndroid,
  Verified,
  Refresh,
  OpenInNew,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/AuthProvider';
import {
  CTWA_CONFIG,
  CTWA_CONNECT_TO_META,
  CTWA_GET_FB_PAGES,
  CTWA_GET_AD_ACCOUNTS,
  CTWA_SAVE_FB_PAGE,
  CTWA_SAVE_AD_ACCOUNT,
  CTWA_WABA_PHONES,
  CTWA_LINK_PHONE_TO_PAGE,
  CTWA_REFRESH_INSTAGRAM,
} from '../../config/urls';

// Meta's own account-linking help doc — used by the "How to link Instagram"
// action when a tenant has no Instagram Business Account linked yet.
const META_IG_LINK_HELP_URL = 'https://www.facebook.com/business/help/898752960195806';

// ─── Step definitions ────────────────────────────────────────────────────────

const STEPS = [
  {
    id: 0,
    stepLabel: 'Facebook Page',
    stepSublabel: 'Connect your page',
    title: 'Connect your Facebook Page',
    description: 'Create or link an existing business page',
    Icon: Facebook,
  },
  {
    id: 1,
    stepLabel: 'Ads Manager',
    stepSublabel: 'Link your account',
    title: 'Connect Meta Ads Manager',
    description: 'Link your existing or new Ads Manager account',
    Icon: ManageAccounts,
  },
  {
    id: 2,
    stepLabel: 'Link Phone',
    stepSublabel: 'Connect WhatsApp number',
    title: 'Link WhatsApp Number',
    description: 'Connect your WhatsApp number to your Facebook Page',
    Icon: PhoneAndroid,
  },
  {
    id: 3,
    stepLabel: 'Ad Campaign',
    stepSublabel: 'Go live with ads',
    title: 'Create CTWA Ad Campaign',
    description: 'Go live with ads that open your WhatsApp chat',
    Icon: Campaign,
  },
];

const FB_PAGES_COLUMNS = [
  { id: 'select', label: '' },
  { id: 'name', label: 'Page Name' },
  { id: 'id', label: 'Page ID' },
  { id: 'category', label: 'Category' },
];

const AD_ACCOUNTS_COLUMNS = [
  { id: 'select', label: '' },
  { id: 'name', label: 'Account Name' },
  { id: 'id', label: 'Account ID' },
  { id: 'currency', label: 'Currency' },
  { id: 'status', label: 'Status' },
];

// ─── Instagram identity card (BB-CTWA-201 / BB-CTWA-202) ───────────────────
// Sits alongside the Facebook Page selection — purely informational, never
// blocks progressing past this step.

const InstagramCard = ({ metaConfig, onConfigRefresh }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem('tenant');

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const igConnected = !!metaConfig?.ig_connected;
  const igUsername = metaConfig?.ig_username;
  const igPic = metaConfig?.ig_profile_pic_url;

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const token = await getValidToken();
      const res = await fetch(CTWA_REFRESH_INSTAGRAM, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to refresh Instagram account.');
      if (onConfigRefresh) await onConfigRefresh();
    } catch (err) {
      setRefreshError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
    <Paper
      variant="outlined"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 2,
        borderRadius: 2, border: `1px solid ${ColorsConstants.primaryGray10}`,
      }}
    >
      <Avatar
        src={igConnected ? igPic : undefined}
        sx={{
          width: 40, height: 40,
          background: igConnected
            ? 'linear-gradient(135deg,#f09433,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888)'
            : ColorsConstants.primaryGray20,
        }}
      >
        {!igConnected && <Instagram sx={{ color: ColorsConstants.primaryGray40 }} />}
      </Avatar>

      <Box flex={1}>
        {igConnected ? (
          <>
            <Typography variant="body2" fontWeight={700}>@{igUsername}</Typography>
            <Chip
              label="Connected"
              size="small"
              sx={{ height: 18, fontSize: 10, fontWeight: 700, mt: 0.25, backgroundColor: '#0E9F6E', color: 'white' }}
            />
          </>
        ) : (
          <>
            <Typography variant="body2" fontWeight={700} color={ColorsConstants.primaryGray50}>
              No Instagram account linked
            </Typography>
            <Typography
              variant="caption"
              onClick={() => window.open(META_IG_LINK_HELP_URL, '_blank', 'noopener,noreferrer')}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.25, cursor: 'pointer',
                color: ColorsConstants.primaryBlue100, fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              How to link Instagram <OpenInNew sx={{ fontSize: 12 }} />
            </Typography>
          </>
        )}
      </Box>

      <IconButton size="small" onClick={handleRefresh} disabled={refreshing} title="Refresh Instagram account">
        {refreshing ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
      </IconButton>
    </Paper>
    {refreshError && (
      <Alert severity="error" sx={{ mt: 1 }}>{refreshError}</Alert>
    )}
    </Box>
  );
};

// ─── Step 1 expanded content ─────────────────────────────────────────────────

const FacebookPageStepContent = ({ onSave, isConnected, metaConfig, onConfigRefresh }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem('tenant');

  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);

  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState(null);
  const [pagesFetched, setPagesFetched] = useState(false);

  const [selectedPageId, setSelectedPageId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const connectToMeta = async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const token = await getValidToken();
      const response = await fetch(CTWA_CONNECT_TO_META, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant,
        },
      });
      if (!response.ok) throw new Error('Failed to connect to Meta');
      const data = await response.json();
      window.open(data.auth_url, '_blank');
    } catch (err) {
      setConnectError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const fetchPages = async () => {
    setPagesLoading(true);
    setPagesError(null);
    try {
      const token = await getValidToken();
      const response = await fetch(CTWA_GET_FB_PAGES, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch pages');
      setPages(data.pages || []);
      setPagesFetched(true);
    } catch (err) {
      setPagesError(err.message);
    } finally {
      setPagesLoading(false);
    }
  };

  const selectPage = async (page) => {
    setSelectedPageId(page.id);
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const token = await getValidToken();
      
      const targetUrl = CTWA_SAVE_FB_PAGE
      
      const response = await fetch(targetUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant,
        },
        body: JSON.stringify({
          fb_page_id: page.id,
          fb_page_name: page.name,
          fb_page_category: page.category || '',
          picture_url: page.picture?.data?.url || '',
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save page');
      
      setSaveSuccess(true);
      if (onSave) onSave(page.id, page.name);
      // The backend resolves the linked Instagram account as a side effect of
      // saving the page — refetch config to pick that up in the IG card.
      if (onConfigRefresh) onConfigRefresh();

    } catch (err) {
      setSaveError(err.message);
      setSelectedPageId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      <Divider sx={{ mb: 2.5 }} />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ButtonOne
          title={connecting ? 'Opening...' : isConnected ? 'Connected to Meta ✓' : 'Connect to Meta'}
          startIcon={<Facebook fontSize="small" />}
          onClick={connectToMeta}
          disabled={connecting || isConnected}
        />
        <ButtonOne
          title="View Connected Pages"
          startIcon={pagesLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Refresh fontSize="small" />}
          onClick={fetchPages}
          disabled={pagesLoading}
          variant="outlined"
        />
        {/* BB-XXXX: Hidden — Create Page requires Meta business_management permission not yet approved */}
      </Stack>

      {isConnected && metaConfig?.ig_permission_granted === false && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <ButtonOne
              title={connecting ? 'Opening...' : 'Reconnect'}
              onClick={connectToMeta}
              disabled={connecting}
              sx={{ height: 30 }}
            />
          }
        >
          Instagram access wasn't granted when this Meta account was connected. Reconnect to enable Instagram placements.
        </Alert>
      )}

      {connectError && <Alert severity="error" sx={{ mb: 2 }}>{connectError}</Alert>}
      {pagesError && <Alert severity="error" sx={{ mb: 2 }}>{pagesError}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Facebook page saved successfully.
        </Alert>
      )}

      {isConnected && <InstagramCard metaConfig={metaConfig} onConfigRefresh={onConfigRefresh} />}

      {pagesFetched && (
        <Box sx={{ position: 'relative' }}>
          {saving && (
            <Box sx={{
              position: 'absolute', inset: 0, zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: 2,
            }}>
              <CircularProgress size={24} />
            </Box>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: ColorsConstants.primaryBlue60 }}>
                  {FB_PAGES_COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={{ fontWeight: 600, color: ColorsConstants.primaryBlue100, width: col.id === 'select' ? 48 : 'auto' }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={FB_PAGES_COLUMNS.length} align="center" sx={{ py: 4, color: ColorsConstants.primaryGray50 }}>
                      No Facebook pages found. Please connect to Meta first.
                    </TableCell>
                  </TableRow>
                ) : (
                  pages.map((page) => (
                    <TableRow
                      key={page.id}
                      hover
                      selected={selectedPageId === page.id}
                      onClick={() => !saving && selectPage(page)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Radio
                          checked={selectedPageId === page.id}
                          onChange={() => selectPage(page)}
                          disabled={saving}
                          size="small"
                          sx={{ color: ColorsConstants.primaryBlue100, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } }}
                        />
                      </TableCell>
                      <TableCell>{page.name}</TableCell>
                      <TableCell>{page.id}</TableCell>
                      <TableCell>{page.category || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

// ─── Step 2 expanded content ─────────────────────────────────────────────────

const AdsManagerStepContent = ({ onSave }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem('tenant');

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetched, setFetched] = useState(false);

  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const fetchAdAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      const response = await fetch(CTWA_GET_AD_ACCOUNTS, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch Ad Accounts');
      setAccounts(data.ad_accounts || []);
      setFetched(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectAccount = async (account) => {
    console.log(account)
    setSelectedAccountId(account.id);
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const token = await getValidToken();
      const response = await fetch(CTWA_SAVE_AD_ACCOUNT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenant,
        },
        body: JSON.stringify({
          ad_account_id: account.id,
          name: account.name,
          currency: account.currency || 'INR',
          account_status: account.account_status || 1,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save Ad Account');
      
      setSaveSuccess(true);
      if (onSave) onSave(account.id, account.name);

    } catch (err) {
      setSaveError(err.message);
      setSelectedAccountId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      <Divider sx={{ mb: 2.5 }} />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ButtonOne
          title="Fetch Ad Accounts"
          startIcon={loading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Refresh fontSize="small" />}
          onClick={fetchAdAccounts}
          disabled={loading}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Ad Account saved successfully! The Wallet is linked.
        </Alert>
      )}

      {fetched && (
        <Box sx={{ position: 'relative' }}>
          {saving && (
            <Box sx={{
              position: 'absolute', inset: 0, zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: 2,
            }}>
              <CircularProgress size={24} />
            </Box>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: ColorsConstants.primaryBlue60 }}>
                  {AD_ACCOUNTS_COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={{ fontWeight: 600, color: ColorsConstants.primaryBlue100, width: col.id === 'select' ? 48 : 'auto' }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={AD_ACCOUNTS_COLUMNS.length} align="center" sx={{ py: 4, color: ColorsConstants.primaryGray50 }}>
                      No Ad Accounts found. Check your Meta Business Manager.
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((acc) => (
                    <TableRow
                      key={acc.id}
                      hover
                      selected={selectedAccountId === acc.id}
                      onClick={() => !saving && selectAccount(acc)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Radio
                          checked={selectedAccountId === acc.id}
                          onChange={() => selectAccount(acc)}
                          disabled={saving}
                          size="small"
                          sx={{ color: ColorsConstants.primaryBlue100, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{acc.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{acc.id}</TableCell>
                      <TableCell>
                        <Chip size="small" label={acc.currency} sx={{ fontSize: 10, height: 20, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                         <Chip 
                            size="small" 
                            label={acc.account_status === 1 ? 'Active' : 'Disabled'} 
                            color={acc.account_status === 1 ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ fontSize: 10, height: 20, fontWeight: 700 }} 
                          />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

// ─── Step 3 expanded content ─────────────────────────────────────────────────

const LinkPhoneStepContent = ({ pageId, onDone }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem('tenant');

  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [selectedPhoneId, setSelectedPhoneId] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkStatus, setLinkStatus] = useState(null); // { type: 'pending'|'error', message? }

  const fetchPhones = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const token = await getValidToken();
      const res = await fetch(CTWA_WABA_PHONES, {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant },
      });
      const data = await res.json();
      if (!res.ok) { setFetchError(data.error || 'Failed to load phone numbers.'); return; }
      setPhones(data.phone_numbers || []);
      if (data.phone_numbers?.length === 1) setSelectedPhoneId(data.phone_numbers[0].id);
    } catch {
      setFetchError('Network error — could not load phone numbers.');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async () => {
    const phone = phones.find((p) => p.id === selectedPhoneId);
    if (!phone) return;
    setLinking(true);
    setLinkStatus(null);
    try {
      const token = await getValidToken();
      const res = await fetch(CTWA_LINK_PHONE_TO_PAGE, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_id: pageId,
          phone_number: phone.display_phone_number.replace(/\s/g, ''),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setLinkStatus({ type: 'error', message: data.error || 'Failed to link phone.' }); return; }
      setLinkStatus({ type: 'pending' });
    } catch {
      setLinkStatus({ type: 'error', message: 'Network error — could not link phone.' });
    } finally {
      setLinking(false);
    }
  };

  return (
    <Box sx={{ mt: 2.5 }}>
      <Divider sx={{ mb: 2.5 }} />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ButtonOne
          title="Load Phone Numbers"
          startIcon={loading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Refresh fontSize="small" />}
          onClick={fetchPhones}
          disabled={loading}
        />
      </Stack>

      {fetchError && <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>}

      {phones.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {phones.map((p) => (
            <Paper
              key={p.id}
              variant="outlined"
              onClick={() => setSelectedPhoneId(p.id)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1,
                borderRadius: 2, cursor: 'pointer',
                border: `2px solid ${selectedPhoneId === p.id ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray10}`,
                backgroundColor: selectedPhoneId === p.id ? ColorsConstants.primaryBlue60 : 'white',
              }}
            >
              <Radio
                checked={selectedPhoneId === p.id}
                onChange={() => setSelectedPhoneId(p.id)}
                size="small"
                sx={{ color: ColorsConstants.primaryBlue100, '&.Mui-checked': { color: ColorsConstants.primaryBlue100 }, p: 0 }}
              />
              <Box flex={1}>
                <Typography variant="body2" fontWeight={700}>{p.display_phone_number}</Typography>
                <Typography variant="caption" color={ColorsConstants.primaryGray50}>{p.verified_name}</Typography>
              </Box>
              <Stack direction="row" gap={0.75}>
                <Chip label={p.status} size="small"
                  sx={{ height: 20, fontSize: 11, fontWeight: 700,
                    backgroundColor: p.status === 'CONNECTED' ? '#DCFCE7' : '#FEF9C3',
                    color: p.status === 'CONNECTED' ? '#16A34A' : '#CA8A04' }} />
                <Chip label={p.quality_rating} size="small"
                  sx={{ height: 20, fontSize: 11, fontWeight: 700,
                    backgroundColor: p.quality_rating === 'GREEN' ? '#DCFCE7' : p.quality_rating === 'YELLOW' ? '#FEF9C3' : '#FEE2E2',
                    color: p.quality_rating === 'GREEN' ? '#16A34A' : p.quality_rating === 'YELLOW' ? '#CA8A04' : '#DC2626' }} />
              </Stack>
            </Paper>
          ))}
        </Box>
      )}

      {linkStatus?.type === 'pending' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700}>Connection request sent!</Typography>
          <Typography variant="body2">
            Status: <strong>Pending Approval</strong>. Go to{' '}
            <strong>Facebook Business Manager → WhatsApp Manager</strong> and approve the connection.
          </Typography>
        </Alert>
      )}
      {linkStatus?.type === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>{linkStatus.message}</Alert>
      )}

      <Stack direction="row" gap={2} sx={{ mt: 2 }}>
        {linkStatus?.type !== 'pending' && (
          <ButtonOne
            title={linking ? 'Linking…' : 'Link to Page'}
            disabled={!selectedPhoneId || linking}
            onClick={handleLink}
          />
        )}
        <ButtonOne
          title={linkStatus?.type === 'pending' ? 'Done — Continue →' : 'Skip for now →'}
          variant={linkStatus?.type === 'pending' ? 'contained' : 'outlined'}
          endIcon={<ArrowForward fontSize="small" />}
          onClick={onDone}
        />
      </Stack>
    </Box>
  );
};

// ─── Main dashboard ───────────────────────────────────────────────────────────

const CTWADashboard = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem('tenant');

  const _perms = localStorage.getItem('permissions') || '';
  const canView    = _perms.includes('ctwa.view');
  const canCreate  = _perms.includes('ctwa.create');
  const canPublish = _perms.includes('ctwa.publish');
  const canPause   = _perms.includes('ctwa.pause');

  const [savedPageId, setSavedPageId] = useState(null);
  const [savedAccountId, setSavedAccountId] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [metaConfig, setMetaConfig] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [step1Expanded, setStep1Expanded] = useState(false);
  const [step2Expanded, setStep2Expanded] = useState(false);
  const [step3Expanded, setStep3Expanded] = useState(false);
  const [showCampaignCreate, setShowCampaignCreate] = useState(false);
  const [showCampaignList, setShowCampaignList] = useState(false);
  const [showCampaignDetail, setShowCampaignDetail] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [resumeDraftId, setResumeDraftId] = useState(null);

  const fetchConfig = async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(CTWA_CONFIG, {
        headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': tenant },
      });
      if (res.ok) {
        const data = await res.json();
        setMetaConfig(data);
        if (data.is_connected && data.fb_page_id && data.ad_account_id) {
          setSavedPageId(data.fb_page_id);
          setSavedAccountId(data.ad_account_id);
          setShowCampaignList(true);
        }
        return data;
      }
    } catch (_) {}
    return null;
  };

  useEffect(() => {
    (async () => {
      await fetchConfig();
      setConfigLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (configLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (showAuditLog) {
    return (
      <Box sx={{ p: { sm: 2, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <CTWAAuditLog onBack={() => { setShowAuditLog(false); setShowCampaignList(true); }} />
      </Box>
    );
  }

  if (showCampaignDetail) {
    return (
      <Box sx={{ p: { sm: 2, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <CTWAManage
          campaignId={selectedCampaignId}
          canPause={canPause}
          onBack={() => { setShowCampaignDetail(false); setSelectedCampaignId(null); setShowCampaignList(true); }}
        />
      </Box>
    );
  }

  if (showCampaignCreate) {
    return (
      <CampaignCreate
        onBack={() => { setShowCampaignCreate(false); setResumeDraftId(null); setShowCampaignList(true); }}
        onPublished={(campaignId) => {
          setShowCampaignCreate(false);
          setResumeDraftId(null);
          setSelectedCampaignId(campaignId);
          setShowCampaignDetail(true);
        }}
        config={metaConfig}
        onConfigRefresh={fetchConfig}
        draftId={resumeDraftId}
        canPublish={canPublish}
      />
    );
  }

  if (showCampaignList) {
    return (
      <Box sx={{ p: { sm: 2, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <CTWACampaignList
          adAccountId={savedAccountId}
          onCreateNew={() => { setShowCampaignList(false); setShowCampaignCreate(true); }}
          onContinueDraft={(draftId) => { setResumeDraftId(draftId); setShowCampaignList(false); setShowCampaignCreate(true); }}
          onViewCampaign={(campaignId) => { setSelectedCampaignId(campaignId); setShowCampaignList(false); setShowCampaignDetail(true); }}
          onViewAuditLog={() => { setShowCampaignList(false); setShowAuditLog(true); }}
          onBack={() => { setShowCampaignList(false); setActiveStep(3); }}
          canCreate={canCreate}
          canPause={canPause}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: 600, backgroundColor: '#F8FAFC' }}>

      {/* ── Header card ── */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${ColorsConstants.primaryGray10}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color={ColorsConstants.primaryBlue100}>
              Click to WhatsApp Ads
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color={ColorsConstants.primaryGray50}>
                Boost ROAS — no Ads Manager needed
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: ColorsConstants.primaryPurple100, cursor: 'pointer', fontWeight: 500 }}
              >
                Learn how →
              </Typography>
            </Stack>
          </Box>
          <Chip
            icon={<Verified sx={{ fontSize: '16px !important', color: `${ColorsConstants.primaryBlue100} !important` }} />}
            label="Meta Certified"
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 4,
              color: ColorsConstants.primaryBlue100,
              borderColor: ColorsConstants.primaryBlue100,
              fontWeight: 500,
            }}
          />
        </Stack>

        <Divider />

        {/* ── Step progress bar ── */}
        <Stack direction="row" alignItems="center" sx={{ mt: 2.5 }}>
          {STEPS.map((step, index) => (
            <Box key={step.id} sx={{ display: 'contents' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  cursor: (index === 0 || index === 1 || index === 2) ? 'pointer' : 'default',
                  backgroundColor: activeStep === index ? ColorsConstants.primaryBlue60 : 'transparent',
                  transition: 'background-color 0.2s',
                  flexShrink: 0,
                }}
                onClick={() => {
                  if (index === 0) setActiveStep(0);
                  if (index === 1) setActiveStep(1);
                  if (index === 2) setActiveStep(2);
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 13,
                    fontWeight: 700,
                    backgroundColor: activeStep === index
                      ? ColorsConstants.primaryBlue100
                      : ColorsConstants.primaryGray20,
                    color: activeStep === index ? 'white' : ColorsConstants.primaryGray60,
                  }}
                >
                  {index + 1}
                </Avatar>
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={activeStep === index ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray50}
                    sx={{ lineHeight: 1.3 }}
                  >
                    {step.stepLabel}
                  </Typography>
                  <Typography variant="caption" color={ColorsConstants.primaryGray40} sx={{ lineHeight: 1 }}>
                    {step.stepSublabel}
                  </Typography>
                </Box>
              </Box>

              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: '1px',
                    backgroundColor: ColorsConstants.primaryGray20,
                    mx: 1,
                  }}
                />
              )}
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* ── Step cards ── */}
      <Stack spacing={1.5}>
        {STEPS.map((step, index) => {
          const isActive = activeStep === index;
          const { Icon } = step;

          return (
            <Paper
              key={step.id}
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${isActive ? ColorsConstants.primaryBlue90 : ColorsConstants.primaryGray10}`,
                backgroundColor: isActive ? ColorsConstants.primaryBlue60 : 'white',
                transition: 'all 0.2s',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    backgroundColor: isActive ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray10,
                    color: isActive ? 'white' : ColorsConstants.primaryGray40,
                    width: 44,
                    height: 44,
                  }}
                >
                  <Icon />
                </Avatar>

                <Box flex={1}>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color={isActive ? ColorsConstants.primaryBlue100 : ColorsConstants.primaryGray60}
                  >
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color={ColorsConstants.primaryGray50}>
                    {step.description}
                  </Typography>
                </Box>

                {/* Right-side action logic */}
                {isActive && index === 0 ? null
                  : isActive && index === 1 ? null
                  : isActive && index === 2 ? null
                  : index === 3 ? (
                    <Stack direction="row" gap={1} sx={{ flexShrink: 0 }}>
                      <ButtonOne
                        title="List Campaigns"
                        variant="outlined"
                        onClick={() => setShowCampaignList(true)}
                      />
                      <ButtonOne
                        title="Create Campaign"
                        endIcon={<ArrowForward fontSize="small" />}
                        onClick={() => setShowCampaignCreate(true)}
                      />
                    </Stack>
                  ) : (
                    <Chip
                      label={`STEP ${index + 1}`}
                      size="small"
                      sx={{
                        backgroundColor: ColorsConstants.primaryGray10,
                        color: ColorsConstants.primaryGray50,
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: 0.5,
                        borderRadius: 1,
                      }}
                    />
                  )}

                {/* Step 1 Start Button */}
                {isActive && index === 0 && (
                  <ButtonOne
                    title={step1Expanded ? 'Connected' : 'Start'}
                    endIcon={!step1Expanded && <ArrowForward fontSize="small" />}
                    onClick={() => setStep1Expanded(true)}
                    disabled={step1Expanded}
                    sx={{ flexShrink: 0 }}
                  />
                )}

                {/* Step 2 Start Button */}
                {isActive && index === 1 && (
                  <ButtonOne
                    title={step2Expanded ? 'Linked' : 'Start'}
                    endIcon={!step2Expanded && <ArrowForward fontSize="small" />}
                    onClick={() => setStep2Expanded(true)}
                    disabled={step2Expanded}
                    sx={{ flexShrink: 0 }}
                  />
                )}

                {/* Step 3 Start Button */}
                {isActive && index === 2 && (
                  <ButtonOne
                    title={step3Expanded ? 'Linking…' : 'Start'}
                    endIcon={!step3Expanded && <ArrowForward fontSize="small" />}
                    onClick={() => setStep3Expanded(true)}
                    disabled={step3Expanded}
                    sx={{ flexShrink: 0 }}
                  />
                )}
              </Stack>

              {isActive && index === 0 && step1Expanded && (
                <FacebookPageStepContent
                  onSave={(id) => {
                    setSavedPageId(id);
                    setTimeout(() => setActiveStep(1), 1500);
                  }}
                  isConnected={!!metaConfig?.is_connected}
                  metaConfig={metaConfig}
                  onConfigRefresh={fetchConfig}
                />
              )}

              {isActive && index === 1 && step2Expanded && (
                <AdsManagerStepContent
                  onSave={(id) => {
                    setSavedAccountId(id);
                    setTimeout(() => setActiveStep(2), 1500);
                  }}
                />
              )}

              {isActive && index === 2 && step3Expanded && (
                <LinkPhoneStepContent
                  pageId={savedPageId}
                  onDone={() => setActiveStep(3)}
                />
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default CTWADashboard;